import { Link, useLocation } from "react-router-dom";
import { Phone, Menu, X, ChevronDown } from "lucide-react";
import { useState } from "react";
import { contactInfo } from "../data";

const navigation = [
  { name: "Forside", href: "/" },
  { 
    name: "Behandlinger og priser", 
    href: "#",
    submenu: [
      { name: "Fodbehandlinger", href: "/behandlinger/fodbehandling" },
      { name: "Udebehandling", href: "/behandlinger/udebehandling" },
      { name: "Tillægsydelser", href: "/behandlinger/tillaeg" },
    ]
  },
  { name: "Om Pernille", href: "/om-pernille" },
  { name: "Kontakt", href: "/kontakt" },
];

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [behandlingerOpen, setBehandlingerOpen] = useState(false);
  const location = useLocation();

  const isItemActive = (item) => {
    if (item.submenu) {
      return item.submenu.some(sub => location.pathname === sub.href);
    }
    return location.pathname === item.href;
  };

  return (
    <header className="absolute top-0 left-0 right-0 z-50 bg-transparent">
      <nav className="mx-auto flex max-w-7xl items-center justify-between p-6 lg:px-8" aria-label="Global">
        <div className="flex lg:flex-1">
          <Link to="/" className="-m-1.5 p-1.5 flex items-center transition-opacity hover:opacity-90">
            <img 
              src="https://assets.auxodev.dk/assets/aec437a7-dbd3-431a-ae4e-b42ebba93fa1/juhler-logo.png" 
              alt="Juhler Fodpleje" 
              className="h-36 lg:h-40 w-auto object-contain brightness-0 invert"
            />
          </Link>
        </div>
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-white"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Åbn menu</span>
            <Menu className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
        <div className="hidden lg:flex lg:items-center lg:gap-x-8">
          {navigation.map((item) => (
            <div key={item.name} className="relative group">
              {item.submenu ? (
                <div className="relative py-2">
                  <button
                    className={`relative flex items-center gap-1 text-sm font-medium leading-6 transition-all duration-200 ${
                      isItemActive(item) 
                        ? "text-white" 
                        : "text-white/70 hover:text-white"
                    }`}
                  >
                    {item.name}
                    <ChevronDown className={`h-4 w-4 transition-transform duration-200 group-hover:rotate-180`} />
                    <span className={`absolute -bottom-1 left-0 right-0 h-0.5 bg-white rounded-full transition-transform duration-300 origin-left ${
                      isItemActive(item) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                    }`} />
                  </button>
                  <div className="absolute -left-4 top-full pt-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 translate-y-2 group-hover:translate-y-0">
                    <div className="w-56 overflow-hidden rounded-2xl bg-white/95 backdrop-blur-sm shadow-2xl ring-1 ring-black/5 p-2">
                      {item.submenu.map((sub) => (
                        <Link
                          key={sub.name}
                          to={sub.href}
                          className={`block px-4 py-2.5 text-sm font-medium rounded-xl transition-all duration-200 ${
                            location.pathname === sub.href
                              ? "bg-stone-100 text-stone-900"
                              : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                          }`}
                        >
                          {sub.name}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <Link
                  to={item.href}
                  className={`relative py-2 text-sm font-medium leading-6 transition-all duration-200 ${
                    isItemActive(item) 
                      ? "text-white" 
                      : "text-white/70 hover:text-white"
                  }`}
                >
                  {item.name}
                  <span className={`absolute -bottom-1 left-0 right-0 h-0.5 bg-white rounded-full transition-transform duration-300 origin-left ${
                    isItemActive(item) ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"
                  }`} />
                </Link>
              )}
            </div>
          ))}
          <div className="flex items-center ml-4 border-l border-white/20 pl-8">
            <a
              href={`tel:${contactInfo.phone}`}
              className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-sm font-semibold text-stone-900 shadow-sm transition-all duration-300 hover:bg-orange-50 hover:scale-105 active:scale-95"
            >
              <Phone className="h-4 w-4" />
              {contactInfo.phoneDisplay}
            </a>
          </div>
        </div>
      </nav>

      {/* Mobile menu */}
      <div className={`lg:hidden ${mobileMenuOpen ? "fixed inset-0 z-50" : "hidden"}`}>
        <div className="fixed inset-y-0 right-0 z-50 w-full overflow-y-auto bg-white px-6 py-6 sm:max-w-sm sm:ring-1 sm:ring-stone-900/10">
          <div className="flex items-center justify-between">
            <Link to="/" className="-m-1.5 p-1.5" onClick={() => setMobileMenuOpen(false)}>
              <img 
                src="/ChatGPT Image 24. apr. 2026, 14.02.37 copy.png" 
                alt="Juhler Fodpleje" 
                className="h-20 w-auto object-contain"
              />
            </Link>
            <button
              type="button"
              className="-m-2.5 rounded-md p-2.5 text-stone-700"
              onClick={() => setMobileMenuOpen(false)}
            >
              <span className="sr-only">Luk menu</span>
              <X className="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div className="mt-6 flow-root">
            <div className="-my-6 divide-y divide-stone-500/10">
              <div className="space-y-2 py-6">
                {navigation.map((item) => (
                  <div key={item.name}>
                    {item.submenu ? (
                      <>
                        <button
                          onClick={() => setBehandlingerOpen(!behandlingerOpen)}
                          className="-mx-3 flex w-full items-center justify-between rounded-lg px-3 py-2 text-base font-semibold leading-7 text-stone-900 hover:bg-stone-50"
                        >
                          {item.name}
                          <ChevronDown className={`h-5 w-5 transition-transform ${behandlingerOpen ? 'rotate-180' : ''}`} />
                        </button>
                        {behandlingerOpen && (
                          <div className="mt-2 space-y-1 pl-4">
                            {item.submenu.map((sub) => (
                              <Link
                                key={sub.name}
                                to={sub.href}
                                className={`block rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                                  location.pathname === sub.href
                                    ? "bg-stone-50 text-stone-900"
                                    : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                                }`}
                                onClick={() => setMobileMenuOpen(false)}
                              >
                                {sub.name}
                              </Link>
                            ))}
                          </div>
                        )}
                      </>
                    ) : (
                      <Link
                        to={item.href}
                        className={`-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 transition-colors ${
                          isItemActive(item) ? "text-stone-900 bg-stone-50" : "text-stone-700 hover:bg-stone-50"
                        }`}
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
              <div className="py-6">
                <a
                  href={`tel:${contactInfo.phone}`}
                  className="flex items-center justify-center gap-2 rounded-full bg-stone-900 px-4 py-3 text-base font-semibold text-white shadow-lg transition-transform active:scale-95"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <Phone className="h-5 w-5" />
                  Ring og book tid
                </a>
                <p className="mt-4 text-center text-sm font-medium text-stone-600">Tlf: {contactInfo.phoneDisplay}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
